import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import styles from './GearComparePage.module.css';
import API_BASE_URL from '../config/api';
import { formatCurrencyAmount } from '../utils/formatCurrency';
import { addToCompare, clearCompare, COMPARE_LIMIT, getCompareIds, isInCompare, removeFromCompare } from '../utils/gearCompare';
import { useTranslation } from 'react-i18next';
import GearCard from '../components/GearCard';
import GearComparePageSkeleton from '../components/GearComparePageSkeleton';
import { getPriceSettings, PRICE_SETTINGS_STORAGE_KEY } from '../utils/priceSettings';

const EXCLUDED_KEYS = new Set([
  'id',
  'name',
  'nameLower',
  'nameLowercase',
  'name_lowercase',
  'name_lowered',
  'brand',
  'brandLower',
  'brandLowercase',
  'brand_lowercase',
  'brand_lowered',
  'price',
  'category',
  'categoryLower',
  'description',
  'image',
  'offers',
  'validatedAt',
  'originalId',
  'merchant',
  'currency',
  'link',
  'affiliateLink',
  'merchantLink',
  'affiliateNetwork',
  'lastUpdated',
  'updatedAt',
  'lastAutoUpdate',
  'feedManaged',
  'isOnSale',
  'saleDiscountPercent',
  'saleSource',
  'saleReferencePrice',
  '_hash',
  'keywords',
  'imageUrls',
  'image_urls',
  'pictureUrls',
  'images',
  'searchName',
  'searchNames',
  'search_name',
  'searchNameLower',
  'searchNameLowered',
  'search_name_lowered',
  'titleNormalized',
  'title_normalized',
  'searchNameNormalized',
  'search_name_normalized',
  'descriptionLower',
  'slug',
  'tags',
  'rank',
  'priceComparison'
]);

const CompareImageGallery = ({ item }) => {
  const images = useMemo(() => {
    let urls = [];
    if (item.image) urls.push(item.image);
    if (Array.isArray(item.imageUrls)) urls.push(...item.imageUrls);
    if (Array.isArray(item.images)) urls.push(...item.images);
    return [...new Set(urls.filter(Boolean))];
  }, [item]);

  const [currentIndex, setCurrentIndex] = useState(0);

  if (images.length === 0) return null;

  const handlePrev = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleNext = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  return (
    <div className={styles.imageGallery}>
      <img src={images[currentIndex]} alt={item.name} className={styles.image} />
      {images.length > 1 && (
        <>
          <button type="button" className={styles.navButtonLeft} onClick={handlePrev} aria-label="Previous image">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>
          <button type="button" className={styles.navButtonRight} onClick={handleNext} aria-label="Next image">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </>
      )}
    </div>
  );
};

const formatLabel = (key) => key
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (char) => char.toUpperCase());

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '-';
  if (typeof value === 'object') return '-';
  return String(value);
};

const parseOfferPrice = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const normalized = Number.parseFloat(value.replace(',', '.').replace(/[^\d.]/g, ''));
    return Number.isFinite(normalized) ? normalized : null;
  }
  return null;
};

const getSortedOffers = (item, settings, locale) => {
  if (!Array.isArray(item?.offers)) return [];
  const offers = item.offers
    .map((offer, index) => {
      const numericPrice = parseOfferPrice(offer?.price);
      const currency = String(offer?.currency || item?.currency || 'EUR').toUpperCase();
      const approxTarget = parseOfferPrice(offer?.approxPriceInTargetCurrency);
      return {
        id: offer?.offerId || `${item?.id || 'offer'}-${index}`,
        seller: offer?.seller || '-',
        currency,
        price: numericPrice,
        approxPriceInTargetCurrency: approxTarget,
        url: offer?.url || ''
      };
    })
    .filter((offer) => offer.price !== null);

  const uniqueOffersMap = new Map();
  for (const offer of offers) {
    const key = `${offer.seller}-${offer.currency}`;
    if (!uniqueOffersMap.has(key) || uniqueOffersMap.get(key).price > offer.price) {
      uniqueOffersMap.set(key, offer);
    }
  }
  const deduplicatedOffers = Array.from(uniqueOffersMap.values());

  const filteredOffers = settings.viewMode === 'prices'
    ? deduplicatedOffers.filter((offer) => settings.selectedPriceCurrencies.includes(offer.currency))
    : deduplicatedOffers;

  const usableOffers = filteredOffers.length > 0 ? filteredOffers : deduplicatedOffers;

  if (settings.viewMode === 'exchange') {
    return usableOffers
      .map((offer) => {
        const converted = Number.isFinite(offer.approxPriceInTargetCurrency)
          ? offer.approxPriceInTargetCurrency
          : (offer.currency === settings.exchangeTargetCurrency ? offer.price : null);
        if (!Number.isFinite(converted)) return null;

        return {
          ...offer,
          mainPrice: formatCurrencyAmount(converted, settings.exchangeTargetCurrency, locale),
          originalPrice: formatCurrencyAmount(offer.price, offer.currency, locale),
          compareValue: converted
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.compareValue - b.compareValue);
  }

  return usableOffers
    .slice()
    .sort((a, b) => a.price - b.price)
    .map((offer) => ({
      ...offer,
      mainPrice: formatCurrencyAmount(offer.price, offer.currency, locale),
      originalPrice: ''
    }));
};

function GearComparePage() {
  const { i18n, t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [allGear, setAllGear] = useState([]);
  const [compareIds, setCompareIds] = useState(() => getCompareIds());
  const [searchQuery, setSearchQuery] = useState('');
  const [message, setMessage] = useState('');
  const [priceSettings, setPriceSettings] = useState(() => getPriceSettings());
  const scrollRef = useRef(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handleWheel = (e) => {
      if (el.scrollWidth <= el.clientWidth) return;
      
      // If holding shift, browser does horizontal scroll natively, let it do its thing
      if (e.shiftKey) return;

      // Translate vertical mouse wheel scrolling to horizontal scroll for this container
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault();
        el.scrollBy({
          left: e.deltaY > 0 ? 150 : -150,
          behavior: 'smooth'
        });
      }
    };

    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', handleWheel);
    };
  }, [compareIds.length, loading]); // Re-attach if content changes significantly or loading finishes

  useEffect(() => {
    setCompareIds(getCompareIds());
  }, []);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key && event.key !== PRICE_SETTINGS_STORAGE_KEY) return;
      setPriceSettings(getPriceSettings());
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchItems = async () => {
      try {
        setLoading(true);
        setError('');
        const requestedCurrency = priceSettings.viewMode === 'exchange'
          ? priceSettings.exchangeTargetCurrency
          : 'EUR';
        const res = await fetch(`${API_BASE_URL}/api/gear?currency=${encodeURIComponent(requestedCurrency)}`);
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data.message || 'Failed to load gear');
        }

        if (isMounted) {
          setAllGear(Array.isArray(data.data) ? data.data : []);
        }
      } catch (e) {
        if (isMounted) {
          setError(e.message || 'Failed to load compare items');
          setAllGear([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchItems();
    return () => {
      isMounted = false;
    };
  }, [priceSettings.exchangeTargetCurrency, priceSettings.viewMode]);

  const compareItems = useMemo(() => {
    const byId = new Map(allGear.map((item) => [String(item.id), item]));
    return compareIds.map((id) => byId.get(String(id))).filter(Boolean);
  }, [allGear, compareIds]);

  const searchResults = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return [];

    return allGear
      .filter((item) => {
        const haystack = `${item.name || ''} ${item.brand || ''} ${item.category || ''} ${item.description || ''}`.toLowerCase();
        return haystack.includes(query);
      })
      .slice(0, 12);
  }, [allGear, searchQuery]);

  const propertyKeys = useMemo(() => {
    const keys = new Set();
    compareItems.forEach((item) => {
      Object.entries(item || {}).forEach(([key, value]) => {
        if (EXCLUDED_KEYS.has(key)) return;
        if (value === null || value === undefined || value === '') return;
        if (typeof value === 'object' && !Array.isArray(value)) return;
        keys.add(key);
      });
    });
    return Array.from(keys).sort((a, b) => a.localeCompare(b));
  }, [compareItems]);

  const handleAdd = (itemId) => {
    const selectedItem = allGear.find((item) => String(item.id) === String(itemId));
    if (!selectedItem?.id) return;

    const result = addToCompare(selectedItem.id, { category: selectedItem.category });
    setCompareIds(result.ids);

    if (!result.ok && result.reason === 'incompatible') {
      const keepOnlyNewItem = window.confirm(
        `You can only compare items from the same category (${result.compareCategory || '-'}). Delete current compare items and add this one?`
      );

      if (!keepOnlyNewItem) {
        setMessage('Please remove current compare items first.');
        return;
      }

      clearCompare();
      const retryResult = addToCompare(selectedItem.id, { category: selectedItem.category });
      setCompareIds(retryResult.ids);

      if (retryResult.ok) {
        setMessage(`Comparison reset. Added item (${retryResult.ids.length}/${COMPARE_LIMIT}).`);
      }
      return;
    }

    if (!result.ok && result.reason === 'limit') {
      setMessage(`You can compare up to ${COMPARE_LIMIT} products.`);
      return;
    }

    if (result.reason === 'exists') {
      setMessage('Item is already in comparison.');
      return;
    }

    setMessage('Item added to comparison.');
  };

  const handleRemove = (id) => {
    const nextIds = removeFromCompare(id);
    setCompareIds(nextIds);
  };

  const handleReset = () => {
    clearCompare();
    setCompareIds([]);
    setSearchQuery('');
    setMessage('Comparison reset.');
  };

  return (
    <>
      <Helmet>
        <title>{t('gear.compareTitle', { defaultValue: 'Compare gear' })}</title>
      </Helmet>
      <div className={styles.page}>
        <div className={styles.header}>
          <h2>{t('gear.compareTitle', { defaultValue: 'Compare gear' })}</h2>
          <div className={styles.headerActions}>
            <Link to="/gear" className={styles.linkButton}>{t('gear.back', { defaultValue: 'Back' })}</Link>
            {(compareIds.length > 0 || searchQuery.trim() || message) && (
              <button type="button" className={styles.clearButton} onClick={handleReset}>
                {t('gear.compareReset', { defaultValue: 'Reset' })}
              </button>
            )}
          </div>
        </div>

        <section className={styles.searchSection}>
          <input
            type="text"
            className={styles.searchInput}
            placeholder={`Search and add (max ${COMPARE_LIMIT})`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {message && <div className={styles.info}>{message}</div>}

          {searchQuery.trim() && (
            <div className={styles.searchResultsGrid}>
              {searchResults.map((item) => {
                const alreadyAdded = isInCompare(item.id);
                return (
                  <div key={item.id} className={styles.searchResultCard}>
                    <GearCard gear={item} />
                    <button
                      type="button"
                      className={styles.addButton}
                      onClick={() => handleAdd(item.id)}
                      disabled={alreadyAdded}
                    >
                      {alreadyAdded ? 'Added' : 'Add'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {loading ? (
          <GearComparePageSkeleton />
        ) : error ? (
          <div className={styles.error}>{error}</div>
        ) : compareItems.length === 0 ? (
          <div className={styles.placeholder}>{t('gear.compareEmpty', { defaultValue: 'No products added to compare yet.' })}</div>
        ) : (
          <div className={styles.comparisonWrapper} ref={scrollRef}>
            <div className={styles.comparisonTable}>
              
              <div className={`${styles.comparisonRow} ${styles.headerRow}`}>
                <div className={styles.rowLabel}></div>
                {compareItems.map((item) => (
                  <div key={`header-${item.id}`} className={styles.comparisonCell}>
                    <div className={styles.cardTop}>
                      <div>
                        <h3 className={styles.name}>{item.name || '-'}</h3>
                        <div className={styles.brand}>{item.brand || '-'}</div>
                      </div>
                      <button type="button" className={styles.removeButton} onClick={() => handleRemove(item.id)}>
                        {t('gear.compareRemove', { defaultValue: 'Remove' })}
                      </button>
                    </div>
                    <CompareImageGallery item={item} />
                  </div>
                ))}
              </div>

              <div className={styles.comparisonRow}>
                <div className={styles.rowLabel}>
                  <span className={styles.rowLabelText}>{t('gear.comparePrice', { defaultValue: 'Price' })}</span>
                </div>
                {compareItems.map((item) => {
                  const sortedOffers = getSortedOffers(item, priceSettings, i18n.language);
                  const bestOffer = sortedOffers[0] || null;
                  const itemPriceValue = priceSettings.viewMode === 'exchange'
                    ? (bestOffer?.mainPrice
                      || (Number.isFinite(parseOfferPrice(item?.priceComparison?.approxPriceInTargetCurrency))
                        ? formatCurrencyAmount(item.priceComparison.approxPriceInTargetCurrency, priceSettings.exchangeTargetCurrency, i18n.language)
                        : '-'))
                    : (bestOffer?.mainPrice
                      || (Number.isFinite(item.price)
                        ? formatCurrencyAmount(item.price, item.currency || 'EUR', i18n.language)
                        : '-'));
                  const itemOriginalValue = priceSettings.viewMode === 'exchange' && bestOffer?.originalPrice
                    ? bestOffer.originalPrice
                    : '';
                  
                  return (
                    <div key={`price-${item.id}`} className={styles.comparisonCell}>
                      <div className={styles.priceValue}>{itemPriceValue}</div>
                      {itemOriginalValue && <div className={styles.mutedPrice}>{itemOriginalValue}</div>}
                    </div>
                  );
                })}
              </div>

              <div className={styles.comparisonRow}>
                <div className={styles.rowLabel}>
                  <span className={styles.rowLabelText}>{t('gear.compareOffers', { defaultValue: 'Offers' })}</span>
                </div>
                {compareItems.map((item) => {
                   const sortedOffers = getSortedOffers(item, priceSettings, i18n.language);
                   return (
                     <div key={`offers-${item.id}`} className={styles.comparisonCell}>
                        <div className={styles.offersList}>
                          {sortedOffers.length === 0 ? (
                            <div className={styles.offerRow}>
                              <span className={styles.offerSeller}>-</span>
                              <span className={styles.offerPrice}>-</span>
                            </div>
                          ) : (
                            sortedOffers.map((offer) => (
                              <div key={offer.id} className={styles.offerRow}>
                                <span className={styles.offerSeller}>{offer.seller || '-'}</span>
                                <span className={styles.offerPrice}>
                                  {offer.mainPrice}
                                  {offer.originalPrice && <span className={styles.mutedPriceInline}> {offer.originalPrice}</span>}
                                </span>
                              </div>
                            ))
                          )}
                        </div>
                     </div>
                   );
                })}
              </div>

              {propertyKeys.map((key) => (
                <div key={`prop-${key}`} className={styles.comparisonRow}>
                  <div className={styles.rowLabel}>
                    <span className={styles.rowLabelText}>{formatLabel(key)}</span>
                  </div>
                  {compareItems.map((item) => (
                    <div key={`prop-${item.id}-${key}`} className={styles.comparisonCell}>
                      <span className={styles.propValue}>{formatValue(item[key])}</span>
                    </div>
                  ))}
                </div>
              ))}

              <div className={styles.comparisonRow}>
                <div className={styles.rowLabel}>
                  <span className={styles.rowLabelText}>{t('gear.compareDescription', { defaultValue: 'Description' })}</span>
                </div>
                {compareItems.map((item) => (
                  <div key={`desc-${item.id}`} className={styles.comparisonCell}>
                    <p className={styles.description}>{item.description || '-'}</p>
                  </div>
                ))}
              </div>

            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default GearComparePage;
